/**
 * 点云资产插件 —— 管理点云的加载、挂载、渲染、拾取全生命周期
 *
 * 职责：
 * 1. 实现 AssetPlugin<PointCloudLoadRequest, PointCloudHandle>，提供资产 CRUD
 * 2. 实现 Configurable<PointCloudRenderConfig>，暴露渲染参数
 * 3. 内置 PotreeV2Loader，仅支持 potree-v2 (metadata.json) 格式
 * 4. 作为 RenderContributor / PickProvider 参与渲染管线和拾取
 */

import * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import type { OctreeGeometryNode } from '@/plugins/pointCloud/core/OctreeGeometry'
import { PointCloudLoadScheduler } from '@/plugins/pointCloud/core/PointCloudLoadScheduler'
import { PointCloudOctree } from '@/plugins/pointCloud/core/PointCloudOctree'
// 可见性计算
import type { VisibilityActivitySnapshot } from '@/plugins/pointCloud/core/UpdateVisibility'
import { updatePointClouds } from '@/plugins/pointCloud/core/UpdateVisibility'
import type { WorkerPool } from '@/plugins/pointCloud/core/WorkerPool'
import { createOctreeLoaderDeps } from '@/plugins/pointCloud/loaders/createOctreeLoaderDeps'
import type { NodeLoadSettledEvent } from '@/plugins/pointCloud/loaders/OctreeLoader'
import {
  PointCloudResidencyManager,
  type PointCloudResidencySnapshot,
  type PointCloudTrimSummary,
} from '@/plugins/pointCloud/PointCloudResidencyManager'
import type { IPointCloudPrivateRenderer } from '@/plugins/pointCloud/rendering/PointCloudPrivateRenderer'
import { PotreeRenderer } from '@/plugins/pointCloud/rendering/PotreeRenderer'
import type { EngineContext } from '@/runtime/EngineContext'
import { ClipTask, DEFAULT_PICK_WINDOW_SIZE } from '@/shared/constants'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type {
  AssetId,
  ClipVolume,
  Configurable,
  PickPointAttributes,
  PickQuery,
  PickResult,
} from '@/shared/types/core'
import type { AssetPlugin } from '@/shared/types/plugin'
import type {
  PointCloudHandle,
  PointCloudLoadRequest,
  PointCloudService,
  RenderActivityContext,
  RenderActivityReport,
  ViewRegistry,
} from '@/shared/types/services'
import type { EngineFrameContext, ViewFrameContext, ViewId } from '@/shared/types/view'
import { generateId, setMaskRecursive } from '@/shared/utils'
import { getPickRayDistance, isPickPointAtOrBeyondMinDistance } from '@/shared/utils/pickDepth'
// 内建 Loader：仅 PotreeV2
import { PotreeV2Loader } from './loaders/PotreeV2Loader'
// 句柄实现
import { createClassificationScheme, OctreePointCloudHandle } from './PointCloudHandle'
// 渲染后端
import { PointCloudRenderBackend } from './PointCloudRenderBackend'

// 本地类型
import type {
  OctreeLoadResult,
  PointCloudAssetRecord,
  PointCloudClassificationColorMap,
  PointCloudEDLOptions,
  PointCloudPluginOptions,
  PointCloudRenderConfig,
  PointCloudRenderConfigPatch,
  PointCloudResidencyOptions,
} from './types'

const log = getLoggerManager().getLogger('pointcloud')
const diagnosticsLog = getLoggerManager().getLogger('pointcloud:diagnostics')
const memoryLog = getLoggerManager().getLogger('pointcloud:memory')
const POINTCLOUD_VISIBILITY_SLOW_MS = 8
const RESIDENCY_SNAPSHOT_INTERVAL_MS = 15000
const RESIDENCY_DELTA_COUNTER_KEYS = [
  'loadStartedCount',
  'initialLoadStartedCount',
  'retryStartedCount',
  'reloadStartedCount',
  'loadCompletedCount',
  'structuralCompletedCount',
  'loadFailedCount',
  'loadAbortedCount',
  'lateResultDroppedCount',
] as const

type WebGPUPointCloudRendererFactory = (
  context: EngineContext,
  getConfig: () => PointCloudRenderConfig,
  getVisibleOctrees: () => PointCloudOctree[]
) => IPointCloudPrivateRenderer

// ==================== Polygon 裁切数据结构 ====================

/** 材质可消费的 polygon 裁切数据，与 PotreeRenderer 对齐 */
interface ClipPolygonData {
  /** 视图矩阵 */
  viewMatrix: THREE.Matrix4
  /** 投影矩阵 */
  projMatrix: THREE.Matrix4
  /** 多边形顶点（投影坐标系下） */
  markers: Array<{ position: THREE.Vector3 }>
  /** 裁切任务类型 */
  clipTask: number
  /** 分组 ID */
  groupId: number
  /** 投影后的 2D 包围盒 */
  aabb: { min: { x: number; y: number }; max: { x: number; y: number } }
  /** 是否已初始化 */
  initialized: boolean
}

interface ClipBoxData {
  box: THREE.Object3D
  inverse: THREE.Matrix4
  position: THREE.Vector3
}

interface PointCloudClipState {
  clipBoxes: ClipBoxData[]
  clipPolygons: ClipPolygonData[]
  clipTask: PointCloudRenderConfig['clipTask']
}

function createClipState(clipTask: PointCloudRenderConfig['clipTask']): PointCloudClipState {
  return {
    clipBoxes: [],
    clipPolygons: [],
    clipTask,
  }
}

// ==================== 错误类型 ====================

/** 格式不支持错误，仅支持 potree-v2 (metadata.json) 格式 */
export class FormatNotSupportedError extends Error {
  /** 触发错误的资源 URL */
  readonly url: string

  constructor(url: string) {
    super(`无法加载 "${url}"：仅支持 potree-v2 (metadata.json) 格式`)
    this.name = 'FormatNotSupportedError'
    this.url = url
  }
}

// ==================== 默认渲染配置 ====================

/** 点云渲染默认配置 */
const DEFAULT_CONFIG: PointCloudRenderConfig = {
  renderStrategy: 'default',
  batchPointBudget: 1_048_576,
  groupedDrawCallColor: false,
  edl: {
    enabled: false,
    strength: 1.0,
    radius: 1.4,
    opacity: 1.0,
  },
  pointSize: 2,
  pointSizeType: 0, // FIXED：固定点大小（与 dev 分支一致）
  pointShape: 0,
  activeAttributeName: 'rgba',
  minNodeSize: 30,
  showBoundingBox: false,
  clipTask: ClipTask.HIGHLIGHT,
  clipMethod: 0,
  classifications: {},
  elevationGradientRepeat: 1,
  isFlipYZ: false,
}

// ==================== PointCloudPlugin ====================

/** 点云资产插件 */
export class PointCloudPlugin
  implements
    AssetPlugin<PointCloudLoadRequest, PointCloudHandle>,
    PointCloudService,
    Configurable<PointCloudRenderConfig>
{
  /** 插件名称标识 */
  readonly name = 'pointCloud'
  readonly graphicsRequirements = {
    backends: ['webgl2', 'webgpu'] as const,
    capabilities: ['surfaceRender', 'multiView'] as const,
  }
  /** 资产类型标识，固定为 'pointCloud' */
  readonly kind = 'pointCloud' as const
  /** 插件优先级，值越小越先执行 */
  readonly priority = 40
  /** 该插件参与的渲染阶段 */
  readonly phases = [RenderPhase.Prepare, RenderPhase.MainOpaque]
  /** 声明的服务依赖列表 */
  readonly dependencies = ['CameraRig', 'RenderPipeline', 'PickingService']
  /** 对外提供的服务列表 */
  readonly provides = ['PointCloudService']

  /** 内置 PotreeV2 加载器 */
  private readonly loader: PotreeV2Loader
  /** 当前 loader 实际使用的 WorkerPool，仅用于读取诊断计数 */
  private readonly workerPool: WorkerPool

  /** 引擎上下文，onInit 时注入 */
  private context!: EngineContext

  /** 已加载资产记录（handle + octree + loadResult 合一） */
  private records = new Map<AssetId, PointCloudAssetRecord>()

  /** 正在进行的加载任务，支持中止 */
  private pendingLoads = new Map<AssetId, AbortController>()

  /** 插件是否已销毁 */
  private disposed = false

  /** 点云节点驻留、顺序、计量与预算策略管理器。 */
  private residency: PointCloudResidencyManager | null = null
  private readonly residencyOptions: PointCloudResidencyOptions

  /** ClipService 原始裁切体，可能同时包含全局和按 view 限定的数据 */
  private sourceClipVolumes: ReadonlyArray<ClipVolume> = []

  /** 仅包含全局裁切体的材质状态，供默认渲染和延迟加载 octree 复用 */
  private globalClipState: PointCloudClipState = createClipState(DEFAULT_CONFIG.clipTask)

  /** 渲染配置 */
  private config: PointCloudRenderConfig = {
    ...DEFAULT_CONFIG,
    edl: { ...DEFAULT_CONFIG.edl },
  }

  /** 是否默认在点云资源 fetch 请求中携带 Cookie 等凭证 */
  private readonly withCredentials: boolean

  /** 渲染后端（onInit 时创建） */
  private renderBackend: IPointCloudPrivateRenderer | null = null

  /** WebGPU 按需加载 factory，避免 three/webgpu 进入 WebGL/pointcloud 默认产物。 */
  private webGpuRendererFactory: WebGPUPointCloudRendererFactory | null = null

  /** PotreeRenderer 引用，供 GPU 拾取使用 */
  private pRenderer: PotreeRenderer | null = null

  /** 当前帧是否已完成可见性刷新，避免同帧多路径重复计算 */
  private preparedFrameNumber = -1
  private preparedViewIds = new Set<string>()
  private preparedRequiredNodesByView = new Map<string, Set<OctreeGeometryNode>>()
  private lastRequiredNodesByView = new Map<string, Set<OctreeGeometryNode>>()
  private preparedLoadCandidatesByView = new Map<string, OctreeGeometryNode[]>()
  private readonly loadScheduler = new PointCloudLoadScheduler()
  private loadSchedulingFrame = -1
  private preparedVisibilityActivity: VisibilityActivitySnapshot | null = null

  /** 当前帧已完成可见性刷新的作用域键 */
  private preparedVisibilityFrameNumber = -1
  private preparedVisibilityKeys = new Set<string>()
  private residencyMaintenanceFrame = -1
  private lastResidencySnapshotDiagnosticAt = Number.NEGATIVE_INFINITY
  private lastLoggedResidencySnapshot: PointCloudResidencySnapshot | null = null
  /** 下一帧可消费的一次性视觉活动原因 */
  private readonly transientActivityReasons = new Set<string>()
  /** 上一次可见性结果签名，仅用于低成本 activity 判断 */
  private lastVisibilitySignature = ''
  /** 是否存在需要继续推进的 profile 请求 */
  private hasActiveProfileRequests = false
  /** 上一帧可见性遍历写入的视觉活动快照 */
  private lastVisibilityActivity: VisibilityActivitySnapshot | null = null
  /** 点云节点重试唤醒 timer */
  private retryWakeupTimer: ReturnType<typeof setTimeout> | null = null
  private retryWakeupAt = Number.POSITIVE_INFINITY

  private readonly frameEndHandler = (event: { engineFrame: EngineFrameContext }) => {
    this.endFrame(
      event.engineFrame.frameNumber,
      event.engineFrame.views.filter((view) => view.visible).map((view) => view.id)
    )
  }

  constructor(options: PointCloudPluginOptions = {}) {
    this.withCredentials = options.withCredentials ?? false
    this.residencyOptions = { ...options.residency }
    const loaderDeps = createOctreeLoaderDeps({
      potreeInstance: {
        numNodesLoading: 0,
        maxNodesLoading: 8,
        workerPool: options.workerPool,
        workerUrl: options.workerUrl,
        workerBaseUrl: options.workerBaseUrl,
        workerMode: options.workerMode,
      },
    })
    this.workerPool = loaderDeps.workerPool
    loaderDeps.onNodeLoadStarted = (event) => this.residency?.recordNodeLoadStarted(event)
    loaderDeps.onNodeLoadSettled = (event) => {
      this.residency?.recordNodeLoadSettled(event)
      this._handleNodeLoadSettled(event)
    }
    this.loader = new PotreeV2Loader(loaderDeps)
  }

  /** WebGPU 下按需加载私有 renderer 模块。 */
  async preload(context: EngineContext): Promise<void> {
    if (context.graphics?.kind !== 'webgpu') {
      return
    }
    const module = await import('./rendering/webgpu/WebGPUPointCloudRenderBackend')
    this.webGpuRendererFactory = (rendererContext, getConfig, getVisibleOctrees) =>
      new module.WebGPUPointCloudRenderBackend(rendererContext, getConfig, getVisibleOctrees)
  }

  rollbackPreload(): void {
    this.webGpuRendererFactory = null
  }

  // ─── Plugin 生命周期 ─────────────────────────────────────

  /**
   * 初始化：创建 LRU 缓存、渲染后端，注册 PointCloudService。
   *
   * @param context - 引擎上下文，提供 renderer、事件、服务注册表等
   */
  onInit(context: EngineContext): void {
    this.context = context
    this.residency = new PointCloudResidencyManager({
      maxResidentPoints: this.residencyOptions.maxResidentPoints,
      maxCachedPoints: this.residencyOptions.maxCachedPoints,
      maxEstimatedResidentBytes: this.residencyOptions.maxEstimatedResidentBytes,
      lowWatermarkRatio: this.residencyOptions.lowWatermarkRatio,
    })
    this.preparedFrameNumber = -1
    this.preparedViewIds.clear()
    this.preparedRequiredNodesByView.clear()
    this.lastRequiredNodesByView.clear()
    this.preparedLoadCandidatesByView.clear()
    this.loadScheduler.reset()
    this.loadSchedulingFrame = -1
    this.preparedVisibilityActivity = null
    this.preparedVisibilityFrameNumber = -1
    this.preparedVisibilityKeys.clear()
    this.residencyMaintenanceFrame = -1
    this.lastResidencySnapshotDiagnosticAt = Number.NEGATIVE_INFINITY
    this.lastLoggedResidencySnapshot = null
    this.transientActivityReasons.clear()
    this.lastVisibilitySignature = ''
    this.hasActiveProfileRequests = false
    this.lastVisibilityActivity = null

    if (context.graphics?.kind === 'webgpu') {
      if (!this.webGpuRendererFactory) {
        throw new UnsupportedGraphicsCapabilityError(
          'pointcloud-webgpu-preload',
          context.graphics.kind,
          'PointCloudPlugin.preload 尚未完成 WebGPU renderer 模块加载'
        )
      }
      this.renderBackend = this.webGpuRendererFactory(
        context,
        () => this.config,
        () => this._getVisibleOctrees()
      )
      this.pRenderer = null
    } else {
      // 创建 WebGL2 渲染后端：PotreeRenderer 由 THREE.WebGLRenderer 构造
      this.pRenderer = new PotreeRenderer(context.renderer)
      this.renderBackend = new PointCloudRenderBackend(
        this.pRenderer,
        () => this.config,
        () => this._getVisibleOctrees()
      )
    }

    context.registerService('PointCloudService', this)
    log.debug(`[PointCloudPlugin] initialized, withCredentials=${this.withCredentials}`)
  }

  /** 启动：注册为渲染贡献者和拾取提供者，订阅可选的裁切体变更 */
  onStart(): void {
    // 注册为 RenderContributor
    const pipeline = this.context.getService('RenderPipeline')
    pipeline.addRenderContributor(this)

    // 注册为 PickProvider
    const picking = this.context.getService('PickingService')
    picking.addProvider(this)
    this.context.events.on('frame.end', this.frameEndHandler)
    log.debug('[PointCloudPlugin] started, renderContributor=true, pickProvider=true')

    // 条件订阅裁切体变更（ClipPlugin 可能未注册，可选依赖）
    if (this.context.hasService('ClipService')) {
      const clipService = this.context.getService('ClipService')
      this.context.disposables.track(
        clipService.onChanged((volumes) => {
          this._applyClipVolumes(volumes)
          this._markVisualActivity('pointcloud.clipVolumes')
        })
      )
      this._applyClipVolumes(clipService.volumes ?? [])
    }
  }

  /** 销毁：中止所有加载、释放所有资产、释放渲染后端和 LRU 缓存 */
  onDestroy(): void {
    if (this.pendingLoads.size > 0 || this.records.size > 0) {
      log.debug(
        `[PointCloudPlugin] destroying, pendingLoads=${this.pendingLoads.size}, assets=${this.records.size}`
      )
    }
    for (const [_id, controller] of this.pendingLoads) {
      controller.abort()
    }
    this.pendingLoads.clear()
    this.disposed = true
    this.context.events.off('frame.end', this.frameEndHandler)
    this.dispose()

    if (this.renderBackend) {
      this.renderBackend.dispose()
    }

    if (this.residency) {
      this.residency.dispose()
    }
    this._logDestroySummary()
    this.residency = null

    // renderBackend 与 pRenderer 保留到最终诊断后，再统一断开引用。
    this.renderBackend = null
    this.pRenderer = null

    this.preparedFrameNumber = -1
    this.preparedViewIds.clear()
    this.preparedRequiredNodesByView.clear()
    this.lastRequiredNodesByView.clear()
    this.preparedLoadCandidatesByView.clear()
    this.loadScheduler.reset()
    this.loadSchedulingFrame = -1
    this.preparedVisibilityActivity = null
    this.preparedVisibilityFrameNumber = -1
    this.preparedVisibilityKeys.clear()
    this.residencyMaintenanceFrame = -1
    this.lastResidencySnapshotDiagnosticAt = Number.NEGATIVE_INFINITY
    this.lastLoggedResidencySnapshot = null
    this._clearRetryWakeup()
    this.transientActivityReasons.clear()
    this.lastVisibilitySignature = ''
    this.hasActiveProfileRequests = false
    this.lastVisibilityActivity = null
  }

  // ─── AssetPlugin 接口 ────────────────────────────────────

  /**
   * 加载点云资产，支持中止和格式检测。
   *
   * @param request - 加载请求，包含 url、id、signal 等
   * @returns 资产句柄，加载或解析失败抛出异常
   */
  async load(request: PointCloudLoadRequest): Promise<PointCloudHandle> {
    if (this.disposed) {
      throw new DOMException('Load aborted', 'AbortError')
    }

    const id = request.id ?? generateId()
    const controller = new AbortController()
    this.pendingLoads.set(id, controller)
    const loadStart = getLoggerManager().now()

    // 外部 signal 联动：外部中止时同步取消内部
    let abortHandler: (() => void) | undefined
    if (request.signal) {
      if (request.signal.aborted) {
        controller.abort()
      } else {
        abortHandler = () => controller.abort()
        request.signal.addEventListener('abort', abortHandler, { once: true })
      }
    }

    log.debug(`[PointCloudPlugin] load started, id=${id}, format=potree-v2, url=${request.url}`)

    try {
      // 仅支持 potree-v2 格式
      if (!this.loader.canLoad(request)) {
        throw new FormatNotSupportedError(request.url)
      }

      const loadRequest = {
        ...request,
        withCredentials: request.withCredentials ?? this.withCredentials,
      }
      const result = await this.loader.load(loadRequest, {
        signal: controller.signal,
        onProgress: (p) =>
          this.context.events.emit('asset.progress', {
            id,
            kind: this.kind,
            progress: p,
            loaded: p,
            total: 1,
          }),
      })

      // 迟到结果丢弃：插件已销毁或加载已中止
      if (this.disposed || controller.signal.aborted) {
        this._disposeLoadResult(result)
        log.debug(`[PointCloudPlugin] load aborted after result, id=${id}, url=${request.url}`)
        throw new DOMException('Load aborted', 'AbortError')
      }

      const handle = this._createHandle(id, result, loadRequest, controller)
      this.context.events.emit('asset.loaded', { id, kind: this.kind })
      this._markVisualActivity('pointcloud.assetLoaded', id)
      diagnosticsLog.info(
        `[PointCloudPlugin] load completed, id=${id}, points=${result.metadata.pointCount}, attributes=${result.metadata.attributes.attributes.length}, elapsed=${(getLoggerManager().now() - loadStart).toFixed(1)}ms`
      )
      log.debug(
        `[PointCloudPlugin] load completed, id=${id}, points=${result.metadata.pointCount}, elapsed=${(getLoggerManager().now() - loadStart).toFixed(1)}ms`
      )
      return handle
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        log.error(
          `[PointCloudPlugin] load failed, id=${id}, url=${request.url}, reason=${
            err instanceof Error ? err.message : String(err)
          }`
        )
        this.context.events.emit('asset.error', {
          id,
          kind: this.kind,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      } else {
        log.debug(`[PointCloudPlugin] load aborted, id=${id}, url=${request.url}`)
      }
      throw err
    } finally {
      if (request.signal && abortHandler) {
        request.signal.removeEventListener('abort', abortHandler)
      }
      if (this.pendingLoads.get(id) === controller) {
        this.pendingLoads.delete(id)
      }
      log.debug(`[PointCloudPlugin] load settled, id=${id}, pendingLoads=${this.pendingLoads.size}`)
    }
  }

  /**
   * 挂载资产到场景图。
   *
   * @param handle - 已加载完成的点云句柄
   */
  mount(handle: PointCloudHandle): void {
    const object3D = handle.object3D
    if (!object3D) return

    if (object3D instanceof THREE.Object3D) {
      setMaskRecursive(object3D, object3D.layers.mask)
    }

    const root = this.context.sceneGraph.getRootForKind('pointCloud')
    root.add(object3D)
    this.context.sceneBounds?.register({
      id: handle.id,
      kind: this.kind,
      object3D,
      getLocalBoundingBox: () => handle.boundingBox,
      getWorldBoundingBoxForFit: () => this._getWorldFitBoundingBox(handle),
      isVisible: () => handle.visible,
    })
    diagnosticsLog.info(
      `[PointCloudPlugin] mounted, id=${handle.id}, visibleNodes=${handle.visibleNodeCount}, visiblePoints=${handle.visiblePointCount}`
    )
    this.context.events.emit('asset.mounted', { id: handle.id, kind: this.kind })
    this._markVisualActivity('pointcloud.mounted', handle.id)
    log.debug(`[PointCloudPlugin] mounted, id=${handle.id}, assets=${this.records.size}`)
  }

  /**
   * 从场景图卸载资产。
   *
   * @param id - 资产唯一标识
   */
  unmount(id: AssetId): void {
    this.context.sceneBounds?.unregister(this.kind, id)
    const record = this.records.get(id)
    if (record?.handle.object3D?.parent) {
      record.handle.object3D.parent.remove(record.handle.object3D)
    }
    this.context.events.emit('asset.unmounted', { id, kind: this.kind })
    this._markVisualActivity('pointcloud.unmounted', id)
    log.debug(`[PointCloudPlugin] unmounted, id=${id}, assets=${this.records.size}`)
  }

  /**
   * 按 ID 获取资产句柄。
   *
   * @param id - 资产唯一标识
   * @returns 对应的句柄，未找到返回 undefined
   */
  getAsset(id: AssetId): PointCloudHandle | undefined {
    return this.records.get(id)?.handle
  }

  /**
   * 获取所有已加载资产。
   *
   * @returns 当前托管的句柄数组，无资产时返回 []
   */
  getAll(): PointCloudHandle[] {
    return Array.from(this.records.values()).map((r) => r.handle)
  }

  /**
   * 设置资产可见性。
   *
   * @param id - 资产唯一标识
   * @param visible - 目标可见性状态
   */
  setVisibility(id: AssetId, visible: boolean): void {
    const record = this.records.get(id)
    if (record) {
      const previousVisible = record.handle.visible
      record.handle.visible = visible
      this.context.events.emit('asset.visibilityChanged', { id, visible })
      this._markVisualActivity('pointcloud.visibility', id)
      if (previousVisible !== visible) {
        if (!visible) {
          this._removeRequiredNodesForGeometry(record.loadResult.octreeGeometry)
          this._removeLoadCandidatesForGeometry(record.loadResult.octreeGeometry)
        }
        diagnosticsLog.info(
          `[PointCloudPlugin] visibility changed, id=${id}, visible=${visible}, previous=${previousVisible}, visibleNodes=${record.handle.visibleNodeCount}, visiblePoints=${record.handle.visiblePointCount}`
        )
      }
      log.debug(`[PointCloudPlugin] visibility changed, id=${id}, visible=${visible}`)
    }
  }

  /**
   * 设置资产所在的渲染图层掩码，并递归应用到子节点。
   *
   * 多视口场景通过 View 的 layerMask 选择显示哪些层，因此不同 View 可以各自只显示
   * 分配给它的模型（A/B/C），互不干扰。
   */
  setLayerMask(id: AssetId, mask: number): boolean {
    const record = this.records.get(id)
    if (!record) return false
    if (!Number.isInteger(mask) || mask <= 0 || mask > 0xffff_ffff) return false
    const object3D = record.handle.object3D
    if (!(object3D instanceof THREE.Object3D)) return false
    setMaskRecursive(object3D, mask)
    this._markVisualActivity('pointcloud.layerMask', id)
    log.debug(`[PointCloudPlugin] layer mask changed, id=${id}, mask=${mask}`)
    return true
  }

  getRenderActivity(context: RenderActivityContext): RenderActivityReport {
    if (context.renderedViewIds.length === 0) {
      this.transientActivityReasons.clear()
      return { needsNextFrame: false, reasons: [] }
    }

    const reasons = Array.from(this.transientActivityReasons)
    this.transientActivityReasons.clear()

    const activity = this.lastVisibilityActivity
    if (activity) {
      if (activity.gpuUploadBacklog > 0) {
        reasons.push('pointcloud.gpuUploadBacklog')
      }
      if (activity.queuedForLoad > 0 || activity.loadsStarted > 0) {
        reasons.push('pointcloud.streaming')
      }
      if (activity.queuedWaitingForParent > 0 || activity.loadedWaitingForParent > 0) {
        reasons.push('pointcloud.parentPending')
      }
    }

    if (this.hasActiveProfileRequests) {
      reasons.push('pointcloud.profileRequest')
    }

    return {
      needsNextFrame: reasons.length > 0,
      reasons,
    }
  }

  /** 主动执行一次预算维护；未超过高水位时不会释放资源。 */
  requestMemoryCleanup(reason = 'manual'): void {
    if (!this.residency) {
      return
    }

    const before = this.residency.getSnapshot()
    const trim = this.residency.maintainBudget()
    const after = this.residency.getSnapshot()
    if (trim.removed > 0) {
      this._markVisualActivity('pointcloud.residencyTrim')
      diagnosticsLog.info(
        `[PointCloudPlugin] memory cleanup requested, reason=${reason}, trigger=${trim.trigger}, removed=${trim.removed}, beforeEntries=${before.residentEntries}, afterEntries=${after.residentEntries}, beforePoints=${before.residentPoints}, afterPoints=${after.residentPoints}, releasedEstimatedBytes=${trim.releasedEstimatedTotalBytes}, assets=${this.records.size}`
      )
    }
  }

  /** 显式结束一帧并驱动一次驻留维护，供无 RenderLoop 的直接集成使用。 */
  endFrame(frameNumber: number, visibleViewIds?: readonly ViewId[]): void {
    this._schedulePreparedNodeLoads(frameNumber, visibleViewIds)
    this._runResidencyMaintenance(frameNumber, visibleViewIds)
  }

  /**
   * 渲染指定阶段，委托给渲染后端。
   *
   * @param frame - 当前视图帧上下文
   * @param phase - 当前渲染阶段
   * @param ctx - 阶段级渲染上下文
   */
  render(frame: ViewFrameContext, phase: RenderPhase, ctx: PhaseRenderContext): void {
    this._applyClipStateToAllMaterials(this._resolveClipState(frame.viewId))
    this._prepareVisibilityForView(frame)
    this.renderBackend?.render(frame, phase, ctx)
  }

  /**
   * 拾取查询：遍历可见八叉树资产，通过 GPU pick 拾取。
   *
   * @param query - 拾取查询，含射线、相机、NDC 与可选视口
   * @returns 距相机最近的命中结果，未命中返回 null
   */
  pick(query: PickQuery): PickResult | null {
    if (!this.renderBackend || !this.context) return null

    this._applyClipStateToAllMaterials(this._resolveClipState(query.viewId))
    this._prepareVisibilityForPick(query)

    const candidates: PickResult[] = []

    for (const [assetId, record] of this.records) {
      if (!record.handle.visible) continue
      if (query.assetIds && !query.assetIds.includes(assetId)) continue

      const result = this._pickOctree(assetId, record.octree, query)
      if (result) candidates.push(result)
    }

    if (candidates.length === 0) return null

    // 返回距相机最近的结果
    candidates.sort((a, b) => a.distance - b.distance)
    return candidates[0]
  }

  /** WebGPU 异步拾取；WebGL2 backend 未实现时回退到同步私有 pick。 */
  async pickAsync(query: PickQuery): Promise<PickResult | null> {
    if (!this.renderBackend || !this.context) return null
    if (!this.renderBackend.pickAsync) {
      return this.pick(query)
    }

    this._applyClipStateToAllMaterials(this._resolveClipState(query.viewId))
    this._prepareVisibilityForPick(query)
    const candidates: PickResult[] = []
    for (const [assetId, record] of this.records) {
      if (!record.handle.visible) continue
      if (query.assetIds && !query.assetIds.includes(assetId)) continue
      const result = await this._pickOctreeAsync(assetId, record.octree, query)
      if (result) candidates.push(result)
    }
    candidates.sort((a, b) => a.distance - b.distance)
    return candidates[0] ?? null
  }

  /** 显式卸载资产载荷，保留资产记录、拓扑和重载能力。 */
  unloadAsset(id: AssetId): void {
    const record = this.records.get(id)
    if (!record || !this.residency) {
      return
    }
    this._removeLoadCandidatesForGeometry(record.loadResult.octreeGeometry)
    const shouldLogMemory = memoryLog.isEnabled(LogLevel.DEBUG)
    const before = shouldLogMemory ? this.residency.getSnapshot() : null
    const summary = this.residency.unloadAsset(id)
    const after = shouldLogMemory ? this.residency.getSnapshot() : null
    record.octree.visibleNodes = []
    record.octree.numVisibleNodes = 0
    record.octree.numVisiblePoints = 0
    if (summary.removed > 0) {
      this._markVisualActivity('pointcloud.assetUnloaded', id)
    }
    if (before && after) {
      memoryLog.debug(
        `event=pointcloud.residency.assetUnload phase=complete assetId=${id} removed=${summary.removed} releasedPoints=${summary.releasedPoints} releasedEstimatedCpuBytes=${summary.releasedEstimatedCpuBytes} releasedEstimatedGpuBytes=${summary.releasedEstimatedGpuBytes} releasedEstimatedTotalBytes=${summary.releasedEstimatedTotalBytes} beforeEntries=${before.residentEntries} afterEntries=${after.residentEntries} beforePoints=${before.residentPoints} afterPoints=${after.residentPoints} beforeEstimatedTotalBytes=${before.estimatedResidentTotalBytes} afterEstimatedTotalBytes=${after.estimatedResidentTotalBytes}`
      )
    }
  }

  /** 终态销毁指定资产及其异步加载所有权。 */
  disposeAsset(id: AssetId): void {
    const pending = this.pendingLoads.get(id)
    if (pending) {
      pending.abort()
      this.pendingLoads.delete(id)
    }
    this.context.sceneBounds?.unregister(this.kind, id)
    const record = this.records.get(id)
    if (!record) {
      return
    }
    const shouldLogMemory = memoryLog.isEnabled(LogLevel.DEBUG)
    const residencyBefore = shouldLogMemory ? this.residency?.getSnapshot() : undefined
    diagnosticsLog.info(
      `[PointCloudPlugin] dispose started, id=${id}, visibleNodes=${record.handle.visibleNodeCount}, visiblePoints=${record.handle.visiblePointCount}, assets=${this.records.size}`
    )
    this._disposeRecord(id, record)
    this.records.delete(id)
    const residencyAfter = shouldLogMemory ? this.residency?.getSnapshot() : undefined
    this.context.events.emit('asset.disposed', { id, kind: this.kind })
    this._markVisualActivity('pointcloud.disposed', id)
    diagnosticsLog.info(`[PointCloudPlugin] disposed, id=${id}, assets=${this.records.size}`)
    if (residencyBefore && residencyAfter) {
      memoryLog.debug(
        `event=pointcloud.residency.assetDispose phase=complete assetId=${id} removed=${Math.max(0, residencyBefore.residentEntries - residencyAfter.residentEntries)} releasedPoints=${Math.max(0, residencyBefore.residentPoints - residencyAfter.residentPoints)} releasedEstimatedTotalBytes=${Math.max(0, residencyBefore.estimatedResidentTotalBytes - residencyAfter.estimatedResidentTotalBytes)} beforeEntries=${residencyBefore.residentEntries} afterEntries=${residencyAfter.residentEntries} beforePoints=${residencyBefore.residentPoints} afterPoints=${residencyAfter.residentPoints} beforeEstimatedTotalBytes=${residencyBefore.estimatedResidentTotalBytes} afterEstimatedTotalBytes=${residencyAfter.estimatedResidentTotalBytes} assets=${this.records.size}`
      )
    }
  }

  /** 兼容终态销毁入口；不传 id 时销毁全部资产。 */
  dispose(id?: AssetId): void {
    if (id !== undefined) {
      this.disposeAsset(id)
    } else {
      if (this.records.size > 0 || this.pendingLoads.size > 0) {
        diagnosticsLog.info(
          `[PointCloudPlugin] dispose all started, assets=${this.records.size}, pendingLoads=${this.pendingLoads.size}`
        )
      }
      for (const controller of this.pendingLoads.values()) {
        controller.abort()
      }
      this.pendingLoads.clear()

      for (const assetId of Array.from(this.records.keys())) {
        this.disposeAsset(assetId)
      }
      this._markVisualActivity('pointcloud.disposed')
      diagnosticsLog.info('[PointCloudPlugin] disposed all assets')
    }
  }

  private _logDestroySummary(): void {
    if (!memoryLog.isEnabled(LogLevel.INFO)) {
      return
    }
    try {
      const renderer = this.pRenderer?.getMemoryDiagnostics?.()
      const backend = this.renderBackend?.getMemoryDiagnostics?.()
      const workers = this.workerPool?.getDiagnostics?.()
      const residency = this.residency?.getSnapshot()
      memoryLog.info(
        `event=pointcloud.memory engineId=${this.context?.memoryDiagnosticId ?? 0} ` +
          `phase=plugin.destroyed scope=plugin elapsedMs=0 ` +
          `assets=${this.records.size} pending=${this.pendingLoads.size} ` +
          `residentEntries=${residency?.residentEntries ?? 0} residentPoints=${residency?.residentPoints ?? 0} ` +
          `estimatedResidentCpuBytes=${residency?.estimatedResidentCpuBytes ?? 0} ` +
          `estimatedResidentGpuBytes=${residency?.estimatedResidentGpuBytes ?? 0} ` +
          `estimatedResidentTotalBytes=${residency?.estimatedResidentTotalBytes ?? 0} ` +
          `peakResidentEntries=${residency?.peakResidentEntries ?? 0} ` +
          `peakResidentPoints=${residency?.peakResidentPoints ?? 0} ` +
          `peakEstimatedResidentCpuBytes=${residency?.peakEstimatedResidentCpuBytes ?? 0} ` +
          `peakEstimatedResidentGpuBytes=${residency?.peakEstimatedResidentGpuBytes ?? 0} ` +
          `peakEstimatedResidentTotalBytes=${residency?.peakEstimatedResidentTotalBytes ?? 0} ` +
          `protectedEntries=${residency?.protectedEntries ?? 0} ` +
          `peakProtectedEntries=${residency?.peakProtectedEntries ?? 0} ` +
          `trimCount=${residency?.trimCount ?? 0} ` +
          `trimRemovedTotal=${residency?.trimRemovedTotal ?? 0} ` +
          `trimReleasedPointsTotal=${residency?.trimReleasedPointsTotal ?? 0} ` +
          `trimReleasedEstimatedBytesTotal=${residency?.trimReleasedEstimatedBytesTotal ?? 0} ` +
          `trimBlockedByProtectionTotal=${residency?.trimBlockedByProtectionTotal ?? 0} ` +
          `assetUnloadCount=${residency?.assetUnloadCount ?? 0} ` +
          `assetUnloadRemovedTotal=${residency?.assetUnloadRemovedTotal ?? 0} ` +
          `loadStartedTotal=${residency?.loadStartedCount ?? 0} ` +
          `initialLoadStartedTotal=${residency?.initialLoadStartedCount ?? 0} ` +
          `retryStartedTotal=${residency?.retryStartedCount ?? 0} ` +
          `reloadStartedTotal=${residency?.reloadStartedCount ?? 0} ` +
          `loadCompletedTotal=${residency?.loadCompletedCount ?? 0} ` +
          `structuralCompletedTotal=${residency?.structuralCompletedCount ?? 0} ` +
          `loadFailedTotal=${residency?.loadFailedCount ?? 0} ` +
          `loadAbortedTotal=${residency?.loadAbortedCount ?? 0} ` +
          `lateResultDroppedTotal=${residency?.lateResultDroppedCount ?? 0} ` +
          `inFlightLoads=${residency?.inFlightLoads ?? 0} ` +
          `peakInFlightLoads=${residency?.peakInFlightLoads ?? 0} ` +
          `vaoDelta=${renderer?.liveVaoEstimate ?? 0} vboDelta=${renderer?.liveVboEstimate ?? 0} ` +
          `workersActive=${workers?.leasedOrCreating ?? 0} workerWaiters=${workers?.waiters ?? 0} ` +
          `hqDepth=${backend?.hqDepthMaterials ?? 0} hqAttribute=${backend?.hqAttributeMaterials ?? 0}`
      )
    } catch {
      // 诊断失败不得阻断插件释放。
    }
  }

  // ─── Configurable 接口 ──────────────────────────────────

  /**
   * 获取当前渲染配置（返回副本）。
   *
   * @returns 渲染配置快照
   */
  getConfig(): PointCloudRenderConfig {
    return {
      ...this.config,
      edl: { ...this.config.edl },
    }
  }

  /**
   * 合并更新渲染配置。
   *
   * @param config - 要覆盖的字段，未提供字段保持原值
   */
  setConfig(config: PointCloudRenderConfigPatch): void {
    const { edl, ...configPatch } = config
    Object.assign(this.config, configPatch)
    if (edl) {
      this.config.edl = { ...this.config.edl, ...edl }
    }
    this._markVisualActivity('pointcloud.config')
    if (log.isEnabled(LogLevel.DEBUG)) {
      const keys = Object.keys(config).join(',')
      log.debug(`[PointCloudPlugin] render config updated, keys=${keys}`)
    }

    // showBoundingBox 仅在创建 octree 时赋值一次，配置变更需回灌到所有已加载点云
    if (config.showBoundingBox !== undefined) {
      for (const record of this.records.values()) {
        record.octree.showBoundingBox = config.showBoundingBox
      }
    }
    if (config.activeAttributeName !== undefined) {
      for (const record of this.records.values()) {
        record.octree.material.activeAttributeName = config.activeAttributeName
      }
    }
    if (config.classifications !== undefined) {
      for (const record of this.records.values()) {
        this._applyServiceClassificationScheme(record, config.classifications)
      }
    }
  }

  /** 仅更新渲染管线的 EDL 状态，不触碰资产级点云配置。 */
  setEDL(enabled: boolean, options: PointCloudEDLOptions = {}): void {
    this.setConfig({ edl: { ...options, enabled } })
  }

  /** 设置所有未配置实例级覆盖的点云所继承的 classification 颜色映射。 */
  setClassificationColorMap(
    mapping: PointCloudClassificationColorMap,
    defaultColor?: THREE.ColorRepresentation
  ): void {
    const currentDefault = this.config.classifications.DEFAULT?.color
    const classifications = createClassificationScheme(mapping, defaultColor, currentDefault)
    this.setConfig({ classifications })
  }

  /** 清除服务级颜色映射，实例级覆盖保持不变。 */
  clearClassificationColorMap(): void {
    this.setConfig({ classifications: {} })
  }

  /**
   * 设置单个点云的包围盒显隐，仅作用于指定资产。
   *
   * @param id - 点云资产 id
   * @param visible - 是否显示包围盒
   */
  setShowBoundingBox(id: AssetId, visible: boolean): void {
    const record = this.records.get(id)
    if (record) {
      record.octree.showBoundingBox = visible
      this._markVisualActivity('pointcloud.boundingBox', id)
    }
  }

  // ─── 私有方法 ───────────────────────────────────────────

  /** 根据加载结果创建资产句柄并存入 records */
  private _createHandle(
    id: AssetId,
    result: OctreeLoadResult,
    request: PointCloudLoadRequest,
    loadController: AbortController
  ): PointCloudHandle {
    result.octreeGeometry.loadSignal = loadController.signal
    const octree = this._createOctree(result, request)
    const handle = new OctreePointCloudHandle(
      id,
      octree,
      request.url,
      result.metadata.pointCount,
      (reason) => this._markVisualActivity(reason, id)
    )
    if (Object.keys(this.config.classifications).length > 0) {
      handle.applyServiceClassificationScheme(this.config.classifications)
    }

    this.records.set(id, { handle, octree, loadController, loadResult: result })
    this.residency?.attachAsset(id, result.octreeGeometry)
    log.debug(
      `[PointCloudPlugin] handle created, id=${id}, points=${result.metadata.pointCount}, assets=${this.records.size}`
    )
    return handle
  }

  private _handleNodeLoadSettled(event: NodeLoadSettledEvent): void {
    if (this.disposed) {
      return
    }

    if (event.status === 'loaded' || event.status === 'structural') {
      this._markVisualActivity('pointcloud.nodeLoaded', event.node.name)
      return
    }

    if (event.status === 'failed') {
      this._markVisualActivity('pointcloud.nodeLoadFailed', event.node.name)
      this._scheduleRetryWakeup(event.node)
      return
    }

    if (event.status === 'late-dropped') {
      diagnosticsLog.debug(
        `event=pointcloud.nodeLoadLateDropped node=${event.node.name} state=${event.node.resourceState}`
      )
    }
  }

  private _scheduleRetryWakeup(node: OctreeGeometryNode): void {
    const retryAt = Number(node.retryAfter)
    if (!Number.isFinite(retryAt)) {
      return
    }

    const now = globalThis.performance?.now?.() ?? Date.now()
    if (retryAt <= now) {
      this._markVisualActivity('pointcloud.retryReady', node.name)
      return
    }

    if (retryAt >= this.retryWakeupAt) {
      return
    }

    this._clearRetryWakeup()
    this.retryWakeupAt = retryAt
    this.retryWakeupTimer = setTimeout(
      () => {
        this.retryWakeupTimer = null
        this.retryWakeupAt = Number.POSITIVE_INFINITY
        this._markVisualActivity('pointcloud.retryReady', node.name)
      },
      Math.max(0, retryAt - now)
    )
  }

  private _clearRetryWakeup(): void {
    if (this.retryWakeupTimer) {
      clearTimeout(this.retryWakeupTimer)
      this.retryWakeupTimer = null
    }
    this.retryWakeupAt = Number.POSITIVE_INFINITY
  }

  private _markVisualActivity(reason: string, detail?: string): void {
    this.transientActivityReasons.add(reason)
    this.context?.renderInvalidation?.invalidate(reason, detail ? { detail } : undefined)
  }

  private _recordVisibilityActivity(
    visibleOctrees: Array<{
      handle: PointCloudHandle
      octree: PointCloudOctree
    }>,
    result: {
      visibleNodes: unknown[]
      numVisiblePoints: number
      activity?: VisibilityActivitySnapshot
    } | null
  ): void {
    if (!result) {
      this.hasActiveProfileRequests = false
      this.lastVisibilityActivity = null
      return
    }

    const signature = visibleOctrees
      .map(({ handle, octree }) => {
        const nodeNames = (octree.visibleNodes ?? [])
          .map((node) => (node as { name?: string }).name ?? '')
          .join(',')
        return `${handle.id}:${octree.numVisibleNodes ?? 0}:${octree.numVisiblePoints ?? 0}:${nodeNames}`
      })
      .join('|')

    if (signature !== this.lastVisibilitySignature) {
      this.lastVisibilitySignature = signature
      this.transientActivityReasons.add('pointcloud.visibilityChanged')
    }

    this.hasActiveProfileRequests = visibleOctrees.some(
      ({ octree }) => (octree.profileRequests?.length ?? 0) > 0
    )
    this.lastVisibilityActivity = 'activity' in result && result.activity ? result.activity : null
  }

  /** 释放加载结果中持有的资源（不含 octree 自身） */
  private _disposeLoadResult(result: OctreeLoadResult): void {
    result.nodeLoader?.dispose()
    result.octreeGeometry.loadSignal = undefined
    result.octreeGeometry.dispose?.()
  }

  /** 释放已注册资产记录，包含场景对象、运行期加载任务和八叉树资源 */
  private _disposeRecord(id: AssetId, record: PointCloudAssetRecord): void {
    record.loadController.abort()
    this._removeRequiredNodesForGeometry(record.loadResult.octreeGeometry)
    this._removeLoadCandidatesForGeometry(record.loadResult.octreeGeometry)
    if (record.handle.object3D?.parent) {
      record.handle.object3D.parent.remove(record.handle.object3D)
    }
    this._disposeLoadResult(record.loadResult)
    record.octree.dispose?.()
    this.residency?.detachAsset(id)
  }

  /** 更新资产节点可见性，返回可见性计算结果 */
  private _updateVisibility(
    visibleOctrees: Array<{
      handle: PointCloudHandle
      octree: PointCloudOctree
    }>,
    camera: THREE.Camera,
    options: {
      emitVisibilityEvents?: boolean
      maxNodesLoading: number
      viewportSize: { width: number; height: number }
      progressContext?: {
        frameNumber: number
        viewId: string
        visibleViewIds: readonly string[]
      }
    }
  ): {
    visibleNodes: unknown[]
    numVisiblePoints: number
    activity?: VisibilityActivitySnapshot
    requiredGeometryNodes: Set<OctreeGeometryNode>
    loadCandidates: OctreeGeometryNode[]
  } | null {
    if (visibleOctrees.length === 0 || !this.residency) {
      this._recordVisibilityActivity([], null)
      return null
    }

    const mgr = getLoggerManager()
    const start = mgr.isPerfEnabled() ? mgr.now() : 0
    const result = updatePointClouds(
      visibleOctrees.map(({ octree }) => octree),
      camera,
      this.residency,
      this.context.pointBudget,
      options.maxNodesLoading,
      options.viewportSize,
      options.progressContext
    )
    if (mgr.isPerfEnabled()) {
      const elapsed = mgr.now() - start
      if (elapsed >= POINTCLOUD_VISIBILITY_SLOW_MS) {
        mgr.writeRecord({
          level: LogLevel.DEBUG,
          namespace: 'pointcloud:visibility:perf',
          message: `slow visibility update ${elapsed.toFixed(1)}ms, assets=${visibleOctrees.length}, visibleNodes=${result?.visibleNodes.length ?? 0}, visiblePoints=${result?.numVisiblePoints ?? 0}`,
          timestamp: mgr.now(),
        })
      }
    }

    if (result && options.emitVisibilityEvents) {
      for (const { handle, octree } of visibleOctrees) {
        this.context.events.emit('pointcloud.visibilityUpdated', {
          id: handle.id,
          visibleNodes: octree.numVisibleNodes ?? 0,
          visiblePoints: octree.numVisiblePoints ?? 0,
        })
      }
    }
    this._recordVisibilityActivity(visibleOctrees, result ?? null)

    return result ?? null
  }

  private _createOctree(
    result: OctreeLoadResult,
    request: PointCloudLoadRequest
  ): PointCloudOctree {
    const octree = new PointCloudOctree(result.octreeGeometry as any)
    octree.name = request.name ?? request.url
    octree.visible = true
    octree.minimumNodePixelSize = this.config.minNodeSize
    octree.showBoundingBox = this.config.showBoundingBox

    // 同步渲染配置到材质
    octree.material.size = this.config.pointSize
    octree.material.pointSizeType = this.config.pointSizeType
    octree.material.shape = this.config.pointShape
    if (this.config.activeAttributeName) {
      octree.material.activeAttributeName = this.config.activeAttributeName
    }
    this._applyClipStateToMaterial(octree.material, this.globalClipState)
    octree.addEventListener?.('transformation_changed', () => {
      this._clearPreparedVisibility()
      this._markVisualActivity('pointcloud.transform')
    })
    return octree
  }

  /** 服务级分类方案只回灌到没有实例级覆盖的真实点云句柄。 */
  private _applyServiceClassificationScheme(
    record: PointCloudAssetRecord,
    classifications: PointCloudRenderConfig['classifications']
  ): void {
    if (record.handle instanceof OctreePointCloudHandle) {
      record.handle.applyServiceClassificationScheme(
        Object.keys(classifications).length > 0 ? classifications : null
      )
      return
    }

    // 兼容外部自定义 handle 与测试替身；真实加载资产始终使用 OctreePointCloudHandle。
    record.octree.material.setClassificationScheme(classifications)
  }

  /** 获取八叉树资产的世界坐标适配包围盒 */
  private _getWorldFitBoundingBox(handle: PointCloudHandle): THREE.Box3 | null {
    const record = this.records.get(handle.id)
    if (record) {
      return this._getOctreeWorldFitBoundingBox(record.octree)
    }
    return handle.boundingBox?.clone() ?? null
  }

  private _getOctreeWorldFitBoundingBox(octree: PointCloudOctree): THREE.Box3 | null {
    const metadata = (
      octree.pcoGeometry?.loader as {
        metadata?: {
          attributes?: Array<{
            name?: string
            min?: number[]
            max?: number[]
          }>
        }
      } | null
    )?.metadata

    const positionAttribute = metadata?.attributes?.find(
      (attribute) =>
        attribute.name === 'position' &&
        Array.isArray(attribute.min) &&
        attribute.min.length === 3 &&
        Array.isArray(attribute.max) &&
        attribute.max.length === 3
    )

    if (typeof octree.updateWorldMatrix === 'function') {
      octree.updateWorldMatrix(true, false)
    } else if (typeof octree.updateMatrixWorld === 'function') {
      octree.updateMatrixWorld(true)
    }

    if (positionAttribute?.min && positionAttribute.max) {
      // metadata 的 position min/max 是绝对坐标且紧贴数据。点云本地坐标 = 绝对坐标 - offset
      // （offset 即 pcoGeometry 加载时减去的基准），世界变换由 octree.matrixWorld 施加。
      // 减 offset 回本地、再乘 matrixWorld：保持紧致尺寸且跟随移动/旋转；
      // 无位姿时 matrixWorld 即 translate(offset)，结果恰好还原为 metadata 绝对 min/max。
      const offset = octree.pcoGeometry?.offset ?? octree.position
      const tight = new THREE.Box3(
        new THREE.Vector3(...positionAttribute.min),
        new THREE.Vector3(...positionAttribute.max)
      )
      tight.min.sub(offset)
      tight.max.sub(offset)
      return tight.applyMatrix4(octree.matrixWorld)
    }

    return octree.boundingBox?.clone().applyMatrix4(octree.matrixWorld) ?? null
  }

  private _pickOctree(
    assetId: AssetId,
    octree: PointCloudOctree,
    query: PickQuery
  ): PickResult | null {
    const { width, height } = this._getViewportSize(query.viewport)
    if (width <= 0 || height <= 0) return null
    const x = THREE.MathUtils.clamp(Math.round((query.ndc.x + 1) * 0.5 * width), 0, width - 1)
    const y = THREE.MathUtils.clamp(Math.round((query.ndc.y + 1) * 0.5 * height), 0, height - 1)

    const raw = this.renderBackend!.pick({
      octree,
      camera: query.camera,
      ray: query.ray,
      width,
      height,
      x,
      y,
      pickWindowSize: query.windowSize ?? DEFAULT_PICK_WINDOW_SIZE,
      minDistance: query.minDistance,
    })

    return this._createPickResult(assetId, raw, query)
  }

  private async _pickOctreeAsync(
    assetId: AssetId,
    octree: PointCloudOctree,
    query: PickQuery
  ): Promise<PickResult | null> {
    const { width, height } = this._getViewportSize(query.viewport)
    if (width <= 0 || height <= 0) return null
    const x = THREE.MathUtils.clamp(Math.round((query.ndc.x + 1) * 0.5 * width), 0, width - 1)
    const y = THREE.MathUtils.clamp(Math.round((query.ndc.y + 1) * 0.5 * height), 0, height - 1)
    const raw = await this.renderBackend!.pickAsync!({
      octree,
      viewId: query.viewId,
      camera: query.camera,
      ray: query.ray,
      width,
      height,
      x,
      y,
      pickWindowSize: query.windowSize ?? DEFAULT_PICK_WINDOW_SIZE,
      minDistance: query.minDistance,
    })
    return this._createPickResult(assetId, raw, query)
  }

  private _createPickResult(
    assetId: AssetId,
    raw: Record<string, any> | null,
    query: PickQuery
  ): PickResult | null {
    if (!raw || !raw.position) return null

    const point = raw.position as THREE.Vector3
    if (!isPickPointAtOrBeyondMinDistance(query.ray, point, query.minDistance)) {
      return null
    }
    const distance = getPickRayDistance(query.ray, point)

    return {
      assetId,
      kind: this.kind,
      point,
      distance,
      attributes: this._extractPickAttributes(raw),
      _raw: raw,
    }
  }

  /** 从 octree pick 原始结果中提取标准化点属性 */
  private _extractPickAttributes(raw: Record<string, any>): PickPointAttributes {
    const attrs: PickPointAttributes = {}

    if ('classification' in raw && raw.classification != null) {
      const classification = Number(raw.classification[0] ?? raw.classification)
      if (Number.isFinite(classification)) {
        attrs.classification = Math.trunc(classification)
      }
    }
    if ('intensity' in raw && raw.intensity != null) {
      attrs.intensity = raw.intensity[0] ?? raw.intensity
    }
    if ('rgba' in raw && raw.rgba != null) {
      const c = raw.rgba
      attrs.rgba = [c[0] ?? 0, c[1] ?? 0, c[2] ?? 0, c[3] ?? 255]
    }
    if ('return number' in raw && raw['return number'] != null) {
      attrs.returnNumber = raw['return number'][0] ?? raw['return number']
    }
    if ('gps-time' in raw && raw['gps-time'] != null) {
      attrs.gpsTime = raw['gps-time'][0] ?? raw['gps-time']
    }

    return attrs
  }

  private _getViewportSize(viewport?: { width: number; height: number }): {
    width: number
    height: number
  } {
    if (viewport) {
      return {
        width: Math.max(1, Math.round(viewport.width)),
        height: Math.max(1, Math.round(viewport.height)),
      }
    }

    const surface = this.context.graphics.surface
    return {
      width: Math.max(1, Math.round(surface.logicalWidth)),
      height: Math.max(1, Math.round(surface.logicalHeight)),
    }
  }

  private _collectVisibleOctrees(): Array<{
    handle: PointCloudHandle
    octree: PointCloudOctree
  }> {
    const visibleOctrees: Array<{
      handle: PointCloudHandle
      octree: PointCloudOctree
    }> = []

    for (const record of this.records.values()) {
      if (!record.handle.visible) continue
      visibleOctrees.push({ handle: record.handle, octree: record.octree })
    }

    return visibleOctrees
  }

  private _removeRequiredNodesForGeometry(geometry: OctreeGeometryNode['octreeGeometry']): void {
    for (const requiredNodesByView of [
      this.preparedRequiredNodesByView,
      this.lastRequiredNodesByView,
    ]) {
      for (const nodes of requiredNodesByView.values()) {
        for (const node of nodes) {
          if (node.octreeGeometry === geometry) {
            nodes.delete(node)
          }
        }
      }
    }
  }

  private _removeLoadCandidatesForGeometry(geometry: OctreeGeometryNode['octreeGeometry']): void {
    for (const [viewId, candidates] of this.preparedLoadCandidatesByView) {
      const remaining = candidates.filter((node) => node.octreeGeometry !== geometry)
      if (remaining.length > 0) {
        this.preparedLoadCandidatesByView.set(viewId, remaining)
      } else {
        this.preparedLoadCandidatesByView.delete(viewId)
      }
    }
  }

  private _prepareVisibilityForView(frame: ViewFrameContext): void {
    if (!this.renderBackend || !this.residency) return
    if (this.preparedFrameNumber !== frame.frameNumber) {
      this.preparedFrameNumber = frame.frameNumber
      this.preparedViewIds.clear()
      this.preparedRequiredNodesByView.clear()
      this.preparedLoadCandidatesByView.clear()
      this.preparedVisibilityActivity = null
    }

    const visibleViews = this._getVisibleViews()
    const preparePerView = visibleViews.length > 1
    if (preparePerView && this.preparedViewIds.has(frame.viewId)) {
      return
    }
    const scopeKey = this._getVisibilityScopeKey(frame.viewId)
    if (this._isPreparedVisibility(frame.frameNumber, scopeKey)) {
      return
    }

    const result = this._updateVisibility(this._collectVisibleOctrees(), frame.camera, {
      emitVisibilityEvents: true,
      maxNodesLoading: preparePerView ? 0 : this.context.maxNodesLoading,
      viewportSize: {
        width: frame.viewRect.width,
        height: frame.viewRect.height,
      },
      progressContext: {
        frameNumber: frame.frameNumber,
        viewId: frame.viewId,
        visibleViewIds:
          visibleViews.length > 0 ? visibleViews.map((view) => view.id) : [frame.viewId],
      },
    })
    const requiredNodes = result?.requiredGeometryNodes ?? new Set<OctreeGeometryNode>()
    this.preparedRequiredNodesByView.set(frame.viewId, requiredNodes)
    this.lastRequiredNodesByView.set(frame.viewId, requiredNodes)
    if (preparePerView) {
      this.preparedLoadCandidatesByView.set(frame.viewId, result?.loadCandidates ?? [])
      this.preparedVisibilityActivity = this._mergeVisibilityActivity(
        this.preparedVisibilityActivity,
        result?.activity
      )
      this.lastVisibilityActivity = this.preparedVisibilityActivity
      this.preparedViewIds.add(frame.viewId)
      return
    }

    this.preparedViewIds.add('__single__')
    this._markPreparedVisibility(frame.frameNumber, scopeKey)
  }

  private _schedulePreparedNodeLoads(
    frameNumber: number,
    visibleViewIds?: readonly ViewId[]
  ): void {
    if (this.disposed || this.loadSchedulingFrame === frameNumber) {
      return
    }
    this.loadSchedulingFrame = frameNumber
    if (this.preparedFrameNumber !== frameNumber || this.preparedLoadCandidatesByView.size === 0) {
      this.preparedLoadCandidatesByView.clear()
      return
    }

    const activeViewIds = visibleViewIds ?? this._getVisibleViews().map((view) => view.id)
    const result = this.loadScheduler.schedule(
      this.preparedLoadCandidatesByView,
      activeViewIds,
      this.context.maxNodesLoading
    )
    this.preparedLoadCandidatesByView.clear()
    if (result.loadsStarted === 0) {
      return
    }

    const activity = this.preparedVisibilityActivity ?? this.lastVisibilityActivity
    if (activity) {
      const updatedActivity = {
        ...activity,
        loadsStarted: activity.loadsStarted + result.loadsStarted,
      }
      this.preparedVisibilityActivity = updatedActivity
      this.lastVisibilityActivity = updatedActivity
    }
    this._markVisualActivity('pointcloud.nodeLoadsScheduled')
    if (log.isEnabled(LogLevel.DEBUG)) {
      log.debug(
        `multi-view node loads scheduled, frame=${frameNumber}, loadsStarted=${result.loadsStarted}, candidatesAttempted=${result.candidatesAttempted}, activeViews=${activeViewIds.length}`
      )
    }
  }

  private _getViewRegistry(): ViewRegistry | null {
    if (!this.context.hasService('ViewRegistry')) {
      return null
    }

    const registry = this.context.getService('ViewRegistry') as Partial<ViewRegistry> | undefined
    if (!registry || typeof registry.list !== 'function') {
      return null
    }

    return registry as ViewRegistry
  }

  private _getVisibleViews(): Array<{ id: string }> {
    const registry = this._getViewRegistry()
    if (!registry) {
      return []
    }

    return registry.list().filter((view) => view.visible !== false)
  }

  private _mergeVisibilityActivity(
    current: VisibilityActivitySnapshot | null,
    next: VisibilityActivitySnapshot | null | undefined
  ): VisibilityActivitySnapshot | null {
    if (!next) {
      return current
    }
    if (!current) {
      return { ...next }
    }
    return {
      queuedForLoad: current.queuedForLoad + next.queuedForLoad,
      loadsStarted: current.loadsStarted + next.loadsStarted,
      gpuUploads: current.gpuUploads + next.gpuUploads,
      gpuUploadBacklog: current.gpuUploadBacklog + next.gpuUploadBacklog,
      loadedWaitingForParent: current.loadedWaitingForParent + next.loadedWaitingForParent,
      queuedWaitingForParent: current.queuedWaitingForParent + next.queuedWaitingForParent,
      queueSize: current.queueSize + next.queueSize,
    }
  }

  private _runResidencyMaintenance(frameNumber: number, visibleViewIds?: readonly ViewId[]): void {
    if (!this.residency || this.disposed) {
      return
    }
    if (this.residencyMaintenanceFrame === frameNumber) {
      return
    }

    this.residencyMaintenanceFrame = frameNumber
    const protectedNodes = new Set<OctreeGeometryNode>()
    const visibleGeometries = new Set(
      this._collectVisibleOctrees().map(({ octree }) => octree.pcoGeometry)
    )
    const registryViewIds = visibleViewIds ?? this._getVisibleViews().map((view) => view.id)
    const hasKnownVisibleViews = visibleViewIds !== undefined || registryViewIds.length > 0
    const activeViewIds = hasKnownVisibleViews
      ? new Set(registryViewIds)
      : new Set([
          ...this.lastRequiredNodesByView.keys(),
          ...this.preparedRequiredNodesByView.keys(),
        ])

    if (hasKnownVisibleViews) {
      for (const viewId of this.lastRequiredNodesByView.keys()) {
        if (!activeViewIds.has(viewId)) {
          this.lastRequiredNodesByView.delete(viewId)
        }
      }
    }

    for (const viewId of activeViewIds) {
      const nodes =
        this.preparedFrameNumber === frameNumber
          ? (this.preparedRequiredNodesByView.get(viewId) ??
            this.lastRequiredNodesByView.get(viewId))
          : this.lastRequiredNodesByView.get(viewId)
      for (const node of nodes ?? []) {
        if (visibleGeometries.has(node.octreeGeometry)) {
          protectedNodes.add(node)
        }
      }
    }
    this.residency.setProtectedNodes(protectedNodes)
    const trim = this.residency.maintainBudget()
    this._logResidencyRuntimeSnapshot(frameNumber, trim)
    if (trim.removed > 0) {
      this._markVisualActivity('pointcloud.residencyTrim')
    }
  }

  private _logResidencyRuntimeSnapshot(frameNumber: number, trim: PointCloudTrimSummary): void {
    if (!this.residency || !memoryLog.isEnabled(LogLevel.DEBUG)) {
      return
    }

    const now = getLoggerManager().now()
    if (
      trim.removed === 0 &&
      now - this.lastResidencySnapshotDiagnosticAt < RESIDENCY_SNAPSHOT_INTERVAL_MS
    ) {
      return
    }

    const snapshot = this.residency.getSnapshot()
    const previous = this.lastLoggedResidencySnapshot
    const deltas = Object.fromEntries(
      RESIDENCY_DELTA_COUNTER_KEYS.map((key) => [key, snapshot[key] - (previous?.[key] ?? 0)])
    ) as Record<(typeof RESIDENCY_DELTA_COUNTER_KEYS)[number], number>

    this.lastResidencySnapshotDiagnosticAt = now
    this.lastLoggedResidencySnapshot = snapshot
    memoryLog.debug(
      `event=pointcloud.residency.snapshot phase=frame.end frame=${frameNumber} assets=${this.records.size} residentEntries=${snapshot.residentEntries} residentPoints=${snapshot.residentPoints} estimatedResidentCpuBytes=${snapshot.estimatedResidentCpuBytes} estimatedResidentGpuBytes=${snapshot.estimatedResidentGpuBytes} estimatedResidentTotalBytes=${snapshot.estimatedResidentTotalBytes} protectedEntries=${snapshot.protectedEntries} protectedResidentPoints=${snapshot.protectedResidentPoints} cachedResidentPoints=${snapshot.cachedResidentPoints} peakResidentEntries=${snapshot.peakResidentEntries} peakResidentPoints=${snapshot.peakResidentPoints} peakEstimatedResidentCpuBytes=${snapshot.peakEstimatedResidentCpuBytes} peakEstimatedResidentGpuBytes=${snapshot.peakEstimatedResidentGpuBytes} peakEstimatedResidentTotalBytes=${snapshot.peakEstimatedResidentTotalBytes} peakProtectedEntries=${snapshot.peakProtectedEntries} cachedPointHighWatermark=${snapshot.cachedPointHighWatermark} cachedPointLowWatermark=${snapshot.cachedPointLowWatermark} pointHighWatermark=${snapshot.pointHighWatermark ?? 'disabled'} pointLowWatermark=${snapshot.pointLowWatermark ?? 'disabled'} estimatedByteHighWatermark=${snapshot.estimatedByteHighWatermark ?? 'disabled'} estimatedByteLowWatermark=${snapshot.estimatedByteLowWatermark ?? 'disabled'} trimTrigger=${trim.trigger} trimSelected=${trim.selected} trimRemoved=${trim.removed} trimBlockedByProtection=${trim.blockedByProtection} trimCount=${snapshot.trimCount} trimRemovedTotal=${snapshot.trimRemovedTotal} trimReleasedPointsTotal=${snapshot.trimReleasedPointsTotal} trimReleasedEstimatedBytesTotal=${snapshot.trimReleasedEstimatedBytesTotal} trimBlockedByProtectionTotal=${snapshot.trimBlockedByProtectionTotal} loadStartedTotal=${snapshot.loadStartedCount} loadStartedDelta=${deltas.loadStartedCount} initialLoadStartedTotal=${snapshot.initialLoadStartedCount} initialLoadStartedDelta=${deltas.initialLoadStartedCount} retryStartedTotal=${snapshot.retryStartedCount} retryStartedDelta=${deltas.retryStartedCount} reloadStartedTotal=${snapshot.reloadStartedCount} reloadStartedDelta=${deltas.reloadStartedCount} loadCompletedTotal=${snapshot.loadCompletedCount} loadCompletedDelta=${deltas.loadCompletedCount} structuralCompletedTotal=${snapshot.structuralCompletedCount} structuralCompletedDelta=${deltas.structuralCompletedCount} loadFailedTotal=${snapshot.loadFailedCount} loadFailedDelta=${deltas.loadFailedCount} loadAbortedTotal=${snapshot.loadAbortedCount} loadAbortedDelta=${deltas.loadAbortedCount} lateResultDroppedTotal=${snapshot.lateResultDroppedCount} lateResultDroppedDelta=${deltas.lateResultDroppedCount} inFlightLoads=${snapshot.inFlightLoads} peakInFlightLoads=${snapshot.peakInFlightLoads} assetUnloadCount=${snapshot.assetUnloadCount} assetUnloadRemovedTotal=${snapshot.assetUnloadRemovedTotal} assetUnloadReleasedPointsTotal=${snapshot.assetUnloadReleasedPointsTotal} assetUnloadReleasedEstimatedBytesTotal=${snapshot.assetUnloadReleasedEstimatedBytesTotal}`
    )
  }

  private _prepareVisibilityForPick(query: PickQuery): void {
    const currentFrameNumber = this.context.frameNumber
    const scopeKey = this._getVisibilityScopeKey(query.viewId)
    if (
      typeof currentFrameNumber === 'number' &&
      this._isPreparedVisibility(currentFrameNumber, scopeKey)
    ) {
      return
    }

    const visibleOctrees = this._collectVisibleOctrees()
    if (visibleOctrees.length === 0) return

    const progressViewId = query.viewId ?? '__pick__'
    const visibleViews = this._getVisibleViews()
    this._updateVisibility(visibleOctrees, query.camera, {
      maxNodesLoading: 0,
      viewportSize: this._getViewportSize(query.viewport),
      progressContext:
        typeof currentFrameNumber === 'number'
          ? {
              frameNumber: currentFrameNumber,
              viewId: progressViewId,
              visibleViewIds:
                visibleViews.length > 0 ? visibleViews.map((view) => view.id) : [progressViewId],
            }
          : undefined,
    })
    if (typeof currentFrameNumber === 'number') {
      this._markPreparedVisibility(currentFrameNumber, scopeKey)
    }
  }

  /** 获取所有可见的八叉树实例（供渲染后端使用） */
  private _getVisibleOctrees(): PointCloudOctree[] {
    const result: PointCloudOctree[] = []
    for (const record of this.records.values()) {
      if (!record.handle.visible) continue
      result.push(record.octree)
    }
    return result
  }

  /** 应用裁切体变更到所有点云材质 */
  private _applyClipVolumes(volumes: ReadonlyArray<ClipVolume>): void {
    this.sourceClipVolumes = Array.from(volumes)
    this.renderBackend?.setClipVolumes?.(this.sourceClipVolumes)
    this.globalClipState = this._buildClipState(this._getGlobalClipVolumes())
    this._clearPreparedVisibility()
    this._applyClipStateToAllMaterials(this.globalClipState)
  }

  /** 从 ClipVolume 构建点云材质可消费的 clip boxes */
  private _resolveClipTask(volumes: ReadonlyArray<ClipVolume>): PointCloudRenderConfig['clipTask'] {
    for (let index = volumes.length - 1; index >= 0; index -= 1) {
      const volume = volumes[index]!
      if (volume.type === 'box' && typeof volume.clipTask === 'number') {
        return volume.clipTask as PointCloudRenderConfig['clipTask']
      }
    }

    for (const volume of volumes) {
      if (typeof volume.clipTask === 'number' && volume.clipTask !== ClipTask.HIGHLIGHT) {
        return volume.clipTask as PointCloudRenderConfig['clipTask']
      }
    }

    for (const volume of volumes) {
      if (typeof volume.clipTask === 'number') {
        return volume.clipTask as PointCloudRenderConfig['clipTask']
      }
    }

    return this.config.clipTask
  }

  private _buildClipBoxes(volumes: ReadonlyArray<ClipVolume>): ClipBoxData[] {
    const boxVolumes = volumes.filter((v) => v.type === 'box')

    // 构建 box clip 数据，过滤退化矩阵（行列式为 0）
    return boxVolumes
      .filter((v) => v.matrix.determinant() !== 0)
      .map((v) => {
        const box = new THREE.Object3D()
        box.matrixAutoUpdate = false
        box.matrix.copy(v.matrix)
        box.matrixWorld.copy(v.matrix)
        box.matrixWorldNeedsUpdate = false
        const inverse = v.matrix.clone().invert()
        const position = new THREE.Vector3().setFromMatrixPosition(v.matrix)
        return { box, inverse, position }
      })
  }

  /**
   * 从 polygon 类型 ClipVolume 构建材质可消费的 clip polygon 数据。
   * 使用单位视图/投影矩阵，顶点保持世界坐标，shader 通过 wvp 将
   * 模型空间点变换到世界空间后在 XY 平面做点在多边形内的判断。
   */
  private _buildClipPolygons(
    volumes: ReadonlyArray<ClipVolume>,
    fallbackClipTask: PointCloudRenderConfig['clipTask']
  ): ClipPolygonData[] {
    const polygonVolumes = volumes.filter(
      (v) => v.type === 'polygon' && v.vertices && v.vertices.length >= 3
    )

    return polygonVolumes.map((v) => {
      const vertices = v.vertices!

      if (v.space === 'screen' && v.viewMatrix && v.projMatrix) {
        const aabb =
          v.aabb ??
          (() => {
            let minX = Infinity
            let minY = Infinity
            let maxX = -Infinity
            let maxY = -Infinity

            for (const vert of vertices) {
              minX = Math.min(minX, vert.x)
              minY = Math.min(minY, vert.y)
              maxX = Math.max(maxX, vert.x)
              maxY = Math.max(maxY, vert.y)
            }

            return {
              min: new THREE.Vector2(minX, minY),
              max: new THREE.Vector2(maxX, maxY),
            }
          })()

        return {
          viewMatrix: v.viewMatrix.clone(),
          projMatrix: v.projMatrix.clone(),
          markers: vertices.map((vert) => ({ position: vert.clone() })),
          clipTask: v.clipTask ?? fallbackClipTask,
          groupId: this._mapGroupId(v.groupId),
          aabb: {
            min: { x: aabb.min.x, y: aabb.min.y },
            max: { x: aabb.max.x, y: aabb.max.y },
          },
          initialized: true,
        }
      }

      // 计算多边形法线（用前三个顶点的叉积）
      const v0 = vertices[0]
      const v1 = vertices[1]
      const v2 = vertices[2]
      const edge1 = new THREE.Vector3().subVectors(v1, v0)
      const edge2 = new THREE.Vector3().subVectors(v2, v0)
      const normal = new THREE.Vector3().crossVectors(edge1, edge2).normalize()

      // 如果法线退化（共线顶点），回退到 Z 轴向下投影
      if (normal.lengthSq() < 1e-10) {
        normal.set(0, 0, 1)
      }

      // 计算质心
      const center = new THREE.Vector3()
      for (const vert of vertices) {
        center.add(vert)
      }
      center.divideScalar(vertices.length)

      // 构建 lookAt 视图矩阵：从法线方向看向质心
      const eye = center.clone().add(normal)
      const viewMatrix = new THREE.Matrix4().lookAt(eye, center, new THREE.Vector3(0, 0, 1))
      // lookAt 返回的是旋转矩阵，需要构建完整视图矩阵
      const viewFull = new THREE.Matrix4().makeRotationFromEuler(
        new THREE.Euler().setFromRotationMatrix(viewMatrix)
      )
      viewFull.setPosition(
        -center.dot(new THREE.Vector3().setFromMatrixColumn(viewFull, 0)),
        -center.dot(new THREE.Vector3().setFromMatrixColumn(viewFull, 1)),
        -center.dot(new THREE.Vector3().setFromMatrixColumn(viewFull, 2))
      )

      // 正交投影矩阵（单位矩阵即可，shader 中做 NDC 除法后保留投影坐标）
      const projMatrix = new THREE.Matrix4().identity()

      // 将顶点变换到投影坐标系
      const projectedVertices: Array<{ position: THREE.Vector3 }> = []
      let minX = Infinity
      let minY = Infinity
      let maxX = -Infinity
      let maxY = -Infinity

      const vpMatrix = projMatrix.clone().multiply(viewFull)
      for (const vert of vertices) {
        const projected = vert.clone().applyMatrix4(vpMatrix)
        projectedVertices.push({ position: projected })

        // 投影后取 XY 计算 AABB（shader 中 testPoint = pointNDC.xy / pointNDC.w）
        const px = projected.x
        const py = projected.y
        if (px < minX) minX = px
        if (py < minY) minY = py
        if (px > maxX) maxX = px
        if (py > maxY) maxY = py
      }

      return {
        viewMatrix: viewFull,
        projMatrix,
        markers: projectedVertices,
        clipTask: v.clipTask ?? fallbackClipTask,
        groupId: this._mapGroupId(v.groupId),
        aabb: { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } },
        initialized: true,
      }
    })
  }

  /** 稳定的 groupId 字符串到 0~7 的映射（shader 最多支持 8 组） */
  private readonly groupIdMap = new Map<string, number>()

  /** 将 ClipVolume 的 string groupId 映射为 shader 需要的 0~7 数字 */
  private _mapGroupId(groupId: string | undefined): number {
    if (!groupId) return 0
    const existing = this.groupIdMap.get(groupId)
    if (existing !== undefined) return existing
    const nextId = this.groupIdMap.size % 8
    this.groupIdMap.set(groupId, nextId)
    return nextId
  }

  private _buildClipState(volumes: ReadonlyArray<ClipVolume>): PointCloudClipState {
    const clipTask = this._resolveClipTask(volumes)
    return {
      clipBoxes: this._buildClipBoxes(volumes),
      clipPolygons: this._buildClipPolygons(volumes, clipTask),
      clipTask,
    }
  }

  private _applyClipStateToAllMaterials(clipState: PointCloudClipState): void {
    for (const record of this.records.values()) {
      this._applyClipStateToMaterial(record.octree.material, clipState)
    }
  }

  private _resolveClipState(viewId?: ViewId): PointCloudClipState {
    if (!viewId || !this._hasScopedClipVolumes()) {
      return this.globalClipState
    }

    return this._buildClipState(this._resolveClipVolumesForScope(viewId))
  }

  private _resolveClipVolumesForScope(viewId?: ViewId): ReadonlyArray<ClipVolume> {
    if (!viewId) {
      return this._getGlobalClipVolumes()
    }

    if (!this._hasScopedClipVolumes()) {
      return this.sourceClipVolumes
    }

    return this.sourceClipVolumes.filter(
      (volume) => volume.viewId == null || volume.viewId === viewId
    )
  }

  private _getGlobalClipVolumes(): ReadonlyArray<ClipVolume> {
    if (!this._hasScopedClipVolumes()) {
      return this.sourceClipVolumes
    }

    return this.sourceClipVolumes.filter((volume) => volume.viewId == null)
  }

  private _hasScopedClipVolumes(): boolean {
    return this.sourceClipVolumes.some((volume) => volume.viewId != null)
  }

  private _isPreparedVisibility(frameNumber: number, scopeKey: string): boolean {
    return (
      this.preparedVisibilityFrameNumber === frameNumber &&
      this.preparedVisibilityKeys.has(scopeKey)
    )
  }

  private _markPreparedVisibility(frameNumber: number, scopeKey: string): void {
    this._clearPreparedVisibility(frameNumber)
    this.preparedVisibilityKeys.add(scopeKey)
  }

  private _clearPreparedVisibility(frameNumber?: number): void {
    if (frameNumber === undefined) {
      this.preparedLoadCandidatesByView.clear()
    }
    if (frameNumber === undefined || this.preparedVisibilityFrameNumber !== frameNumber) {
      this.preparedVisibilityFrameNumber = frameNumber ?? -1
      this.preparedVisibilityKeys.clear()
    }
  }

  private _getVisibilityScopeKey(viewId?: ViewId): string {
    if (!this._hasScopedClipVolumes()) {
      return 'shared'
    }

    return viewId ? `view:${viewId}` : 'global'
  }

  /** 将当前裁切状态应用到指定点云材质 */
  private _applyClipStateToMaterial(
    material: {
      setClipBoxes(clipBoxes: ClipBoxData[]): void
      setClipPolygons(clipPolygons: ClipPolygonData[], count: number): void
      clipTask: PointCloudRenderConfig['clipTask']
      clipMethod: PointCloudRenderConfig['clipMethod']
    },
    clipState: PointCloudClipState
  ): void {
    material.setClipBoxes(clipState.clipBoxes)
    material.setClipPolygons(clipState.clipPolygons, clipState.clipPolygons.length)
    material.clipTask = clipState.clipTask
    material.clipMethod = this.config.clipMethod
  }
}
